import { Type } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Review } from '@luggagehero/shared/interfaces';

import { getEffectiveExternalReviewUrl } from '../../../../../utils/url.validators';
import { BookingActionInfo } from '../booking-actions/booking-action-info';
import { LeaveFeedbackActionBaseComponent } from '../booking-actions/leave-feedback-action.base-component';
import { BookingStep } from './booking-step';

export abstract class LeaveFeedbackBaseStep extends BookingStep {
  //
  // We only know the base action components here, so force the derived class
  // to provide the specific types
  //
  protected abstract get leaveFeedbackActionComponentType(): Type<LeaveFeedbackActionBaseComponent>;

  getIsVisible(): boolean {
    // AM-4364 — hide the review step for no-show bookings (the traveler didn't use the service,
    // often charged without showing up). A no-show can be PAID or CHECKED_OUT, so gate on the
    // status-agnostic `chargedNoShow` flag, alongside the existing CONFIRMED/CANCELLED exclusion.
    if (this.booking.chargedNoShow) {
      return false;
    }
    return !['CONFIRMED', 'CANCELLED'].includes(this.booking.status);
  }

  getTitle(): string {
    return this.translate('RATE_YOUR_EXPERIENCE');
  }

  getSubtitle(): string {
    if (this.getIsCompleted()) {
      return this.translate('FEEDBACK_RECEIVED');
    }
    return `${this.translate('HOW_WAS_YOUR_EXPERIENCE_WITH')} ${this.booking.shopName}?`;
  }

  getIsSubtitleHidden(): boolean {
    return ['CONFIRMED', 'CHECKED_IN', 'CHECKED_OUT'].includes(this.booking.status);
  }

  getIsActive(): boolean {
    return this.booking.status === 'PAID' && !this.isCompleted;
  }

  getIsCompleted(): boolean {
    if (!this.shop) {
      return false;
    }
    if (getEffectiveExternalReviewUrl(this.shop) && !AppConfig.IS_EXTERNAL_REVIEW_DISABLED) {
      return this.booking.feedbackReceived;
    }
    if (!this.review) {
      return false;
    }
    // AM-4364 — require explicit publish (Submit / Skip CTA). A rating alone keeps the step
    // "in progress" so the user can resume the form (e.g., add a comment after tapping a
    // star earlier and leaving).
    if (this.review.published) {
      return true;
    }
    if (this.review.feedback) {
      return true;
    }
    return false;
  }

  getIsFailed(): boolean {
    return false;
  }

  getActionInfo(): BookingActionInfo {
    if (['CONFIRMED', 'CHECKED_IN'].includes(this.booking.status)) {
      return null;
    }
    if (this.getIsCompleted()) {
      // AM-4364 — always offer "Edit review". Previously the CTA fell back to
      // generic "Feedback" when the shop had an externalReviewUrl, which routed
      // users to leave a Google review. The in-modal pickup flow now hosts the
      // Google CTA at the time of pickup; the timeline link is purely for
      // editing the in-app review afterwards.
      return (
        new BookingActionInfo('EditReviewFromLeaveFeedbackStep')
          .withCallToAction(this.translate('EDIT_REVIEW'))
          .withAction({ componentToShow: this.leaveFeedbackActionComponentType })
          .withTitle(`${this.translate('YOUR_REVIEW_OF_SHOP_NAME')} ${this.booking.shopName}`)
          // AM-4364 — editing an existing review always has a rating, so render the review
          // fullscreen with its own header (no shared chrome), consistent with the rating strip.
          .withFullscreenReview(true)
      );
    }
    return (
      new BookingActionInfo('LeaveFeedbackFromLeaveFeedbackStep')
        .withCallToAction(this.translate('LEAVE_FEEDBACK'))
        .withAction({ componentToShow: this.leaveFeedbackActionComponentType })
        .withTitle(this.translate('FEEDBACK'))
        // AM-4364 — fullscreen + chrome-less like the other review surfaces. The no-rating
        // state owns its escape hatch via the docked dismiss link in lh-review.
        .withFullscreenReview(true)
        .withReviewChangeHandler((r) => this.onFeedbackReceived(r))
    );
  }

  getIsActionCollapsed(): boolean {
    return !this.previousStep.isCompleted || this.isCompleted;
  }

  private onFeedbackReceived(_review: Review) {
    void this.reloadBooking();
  }
}
